import { Api } from '@monorepo/shared/apiClient';
import {
  LogCacheEvents,
  logCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/logCacheRegister';
import {
  LogFrequencyRequirementCacheEvents,
  logFrequencyRequirementCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/logFrequencyRequirementCacheRegister';
import {
  ResourceCacheEvents,
  resourceCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/resourceCacheRegister';
import {
  ResourceTypeCacheEvents,
  resourceTypeCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/resourceTypeCacheRegister';
import {
  WorkflowCacheEvents,
  workflowCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/workflowCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type KeyParams = Api.FetchProjectDatasetIdsParams;
const KEY_START = 'project-datasets';

type KeyPartsAllDefined = [string, string, string];
type KeyPartsPartial = [string, string | undefined, string | undefined];

function getKeyPartsInOrder(keyParams: KeyParams): KeyPartsAllDefined;
function getKeyPartsInOrder(keyParams: Partial<KeyParams>): KeyPartsPartial;
function getKeyPartsInOrder(
  keyParams: KeyParams | Partial<KeyParams>,
): KeyPartsAllDefined | KeyPartsPartial {
  return [KEY_START, keyParams.organizationId, keyParams.projectId];
}

export const createKey = (keyParams: KeyParams) =>
  getKeyPartsInOrder(keyParams);

type Fetcher = Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchProjectDatasetIds
>;

const fetcher: Fetcher = (_, organizationId, projectId) =>
  Api.fetchProjectDatasetIds({ organizationId, projectId });

type UseDatasetsParams = Partial<Api.FetchProjectDatasetIdsParams> & {
  config?: QueryConfig<Api.FetchProjectDatasetIdsResponse, Api.ErrorResponse>;
};

export function useProjectEnabledDatasetIds({
  config: inputConfig,
  organizationId,
  projectId,
}: UseDatasetsParams) {
  const isEnabled = !!organizationId && !!projectId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled ? createKey({ organizationId, projectId }) : undefined;

  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return {
    ...queryInfo,
    projectDatasetIds: data,
  };
}

type InvalidateCacheParams = Pick<KeyParams, 'organizationId'> &
  Partial<Omit<KeyParams, 'organizationId'>>;

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation(getKeyPartsInOrder(keyParams)),
  );
}

const cacheRegisterInvalidator = {
  hookName: 'useProjectEnabledDatasetIds',
  callback: (keyParams: InvalidateCacheParams) => invalidateCache(keyParams),
};

logCacheRegister(
  [
    LogCacheEvents.CREATE,
    LogCacheEvents.UPDATE, // Log can be published or un-published
    LogCacheEvents.DELETE,
  ],
  cacheRegisterInvalidator,
);

logFrequencyRequirementCacheRegister(
  [
    LogFrequencyRequirementCacheEvents.CREATE,
    LogFrequencyRequirementCacheEvents.DELETE,
  ],
  cacheRegisterInvalidator,
);

resourceTypeCacheRegister(
  [ResourceTypeCacheEvents.CREATE, ResourceTypeCacheEvents.DELETE],
  cacheRegisterInvalidator,
);

resourceCacheRegister(
  [
    ResourceCacheEvents.CREATE, // it could be the first site-managed resource for a project
    ResourceCacheEvents.DELETE, // it could be the last site-managed resource for a project
  ],
  cacheRegisterInvalidator,
);

workflowCacheRegister(
  [WorkflowCacheEvents.CREATE, WorkflowCacheEvents.DELETE],
  cacheRegisterInvalidator,
);
